import React, { useState } from 'react';
import Auralogo from '@/mobile/assets/AURA_logo.png';
import CircularProgress from '@mui/material/CircularProgress';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, MenuItem, Select, Typography } from '@mui/material';
import BaseDialog from '@/components/ui/BaseDialog';
import { useAppContext } from '@/provider/contexts';
import BaseButton from '@/components/ui/BaseButton';

interface HeaderProps {
    storeName: string;
    isLoadingStore: boolean;
    currentLanguage: string;
    onLanguageChange: (lang: string) => void;
}

const Header: React.FC<HeaderProps> = ({ storeName, isLoadingStore, currentLanguage, onLanguageChange }) => {
    const navigate = useNavigate();
    const { t } = useTranslation('common');
    const { setManageStoreId } = useAppContext();
    const [openSwitchStoreChange, setOpenSwitchStoreChange] = useState(false);
    const hasMultiStore = localStorage.getItem('hasMultiStore');
    const canChangeStore = hasMultiStore === 'true';
    const handleBackClick = () => {
        navigate('/store_management/overview');
    };

    const languages = [
        { code: 'en', label: t('English') },
        { code: 'zh-Hant', label: t('繁體中文') },
        { code: 'ja', label: t('日本語') },
        { code: 'fr', label: t('Français') },
        { code: 'es', label: t('Español') },
        { code: 'de', label: t('Deutsch') },
        { code: 'ko', label: t('한국어') },
        { code: 'ar', label: t('العربية') },
    ];

    const handleStoreChange = () => {
        if (hasMultiStore === 'true') {
            setOpenSwitchStoreChange(true);
        }
    };

    return (
        <>
            <Box
                sx={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bgcolor: 'background.paper',
                    zIndex: (theme) => theme.zIndex.appBar + 1,
                }}
            >
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <BaseButton
                        fullWidth
                        variant="text"
                        onClick={handleBackClick}
                        sx={{
                            justifyContent: 'flex-start',
                            fontSize: 11,
                            fontWeight: 700,
                            textTransform: 'none',
                            height: 28,
                            py: 1,
                        }}
                    >
                        ← {t('Back to Store Overview')}
                    </BaseButton>
                </Box>
                <Box
                    sx={{
                        display: 'grid',
                        alignItems: 'center',
                        gridTemplateColumns: 'auto 1fr auto',
                        columnGap: 1,
                        p: 1,
                    }}
                >
                    <Box
                        component="img"
                        src={Auralogo}
                        alt="AURA Logo"
                        sx={{
                            height: 24,
                            objectFit: 'contain',
                            display: 'block',
                        }}
                    />

                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <Select
                            size="small"
                            value={currentLanguage || 'en'}
                            onChange={(e) => onLanguageChange(e.target.value)}
                            sx={{
                                fontSize: '12px',
                                fontWeight: 600,
                                width: 'clamp(120px, 25vw, 170px)',
                                '& .MuiSelect-select': {
                                    py: 0.75,
                                },
                            }}
                        >
                            {languages.map((lang) => (
                                <MenuItem key={lang.code} value={lang.code} sx={{ fontSize: '12px', fontWeight: 600 }}>
                                    {t(lang.label)}
                                </MenuItem>
                            ))}
                        </Select>
                    </Box>

                    <Box
                        onClick={canChangeStore ? handleStoreChange : undefined}
                        sx={{
                            maxWidth: '15vw',
                            fontSize: 12,
                            color: 'text.secondary',
                            cursor: canChangeStore ? 'pointer' : 'default',
                            pointerEvents: canChangeStore ? 'auto' : 'none',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            textAlign: 'right',
                            pr: 1,
                        }}
                    >
                        {isLoadingStore ? (
                            <CircularProgress size={16} />
                        ) : (
                            <Box
                                component="span"
                                sx={{
                                    display: '-webkit-box',
                                    WebkitBoxOrient: 'vertical',
                                    WebkitLineClamp: 2,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    lineHeight: 1.2,
                                    wordBreak: 'break-word',
                                }}
                            >
                                {storeName || t('Store not found')}
                            </Box>
                        )}
                    </Box>
                </Box>
            </Box>

            <BaseDialog
                maxWidth="xs"
                open={openSwitchStoreChange}
                dialogTitle={t('Change Stores')}
                confirmButtonText={t('YES')}
                cancelButtonText={t('NO')}
                onConfirm={() => {
                    setManageStoreId(null);
                    navigate('/mobile/select-store', { replace: true });
                }}
                onClose={() => setOpenSwitchStoreChange(false)}
            >
                <Typography>{t('Are you sure you want to change Stores ?')}</Typography>
            </BaseDialog>
        </>
    );
};

export default Header;
